import { useRef, useEffect } from "react";
import ChevronLeftIcon from "@heroicons/react/24/outline/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";

/** Get today's date key in YYYY-MM-DD */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Get yesterday's date key */
function yesterdayKey(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** Format a date key for display */
function formatDateLabel(dateKey: string): string {
  const today = todayKey();
  const yesterday = yesterdayKey();

  if (dateKey === today) return "Today";
  if (dateKey === yesterday) return "Yesterday";

  // Format as "Mon DD" (e.g., "Feb 12")
  const date = new Date(dateKey + "T12:00:00Z"); // noon UTC to avoid timezone issues
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface ChatDateTabsProps {
  sessions: string[];
  activeDate: string;
  onSelectDate: (date: string) => void;
}

export function ChatDateTabs({ sessions, activeDate, onSelectDate }: ChatDateTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  // Build tab list: all session dates + today (always present), sorted ascending
  const today = todayKey();
  const allDates = new Set([...sessions, today]);
  const sortedDates = [...allDates].sort();

  // Auto-scroll to active tab on mount and when activeDate changes
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeDate]);

  // Only render if there are past sessions (more than just today)
  if (sortedDates.length <= 1) return null;

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = 150;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex items-center border-b border-gray-200 shrink-0">
      {/* Left scroll button */}
      <button
        type="button"
        onClick={() => scroll("left")}
        className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
        aria-label="Scroll left"
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </button>

      {/* Scrollable tabs */}
      <div
        ref={scrollRef}
        className="flex-1 flex overflow-x-auto scrollbar-hide gap-1 px-1 py-1.5"
      >
        {sortedDates.map((dateKey) => {
          const isActive = dateKey === activeDate;
          return (
            <button
              key={dateKey}
              ref={isActive ? activeTabRef : undefined}
              type="button"
              onClick={() => onSelectDate(dateKey)}
              className={`
                shrink-0 px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap
                ${isActive
                  ? "bg-brand-100 text-brand-700"
                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }
              `}
            >
              {formatDateLabel(dateKey)}
            </button>
          );
        })}
      </div>

      {/* Right scroll button */}
      <button
        type="button"
        onClick={() => scroll("right")}
        className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
        aria-label="Scroll right"
      >
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
